// src/prototype/enterprise/enterpriseTabs.ts
// ============================================================
// Single source of truth for the Enterprise sub-nav tabs AND their context-row copy.
// The navy sub-nav (EnterpriseSubNav) and the neutral journey bar's description row
// (EnterpriseJourneyBar) both key off this, so the tab list and its copy can't drift.
//
// Each tab's copy mirrors the two-part storefront step description (a bold thesis + one
// explanatory sentence). The design (Enterprise Prototype.dc.html) has no copy for this
// row — its journey bar has no context row — so these use the approved product wording.
// ============================================================

export interface EnterpriseTab {
  to: string;
  label: string;
}

export const ENTERPRISE_TABS: EnterpriseTab[] = [
  { to: '/enterprise/portfolio', label: 'Portfolio' },
  { to: '/enterprise/scenarios', label: 'Scenarios' },
  { to: '/enterprise/decisions', label: 'Decisions' },
  { to: '/enterprise/reports', label: 'Reports' },
];

/**
 * Context-row copy per tab — same shape/rhythm as a storefront step description: a left block
 * (bold thesis + explanatory sentence) plus a short right-side forward-looking note (rendered
 * under the Execute step, mirroring the storefront's reinforcement line).
 */
export interface EnterpriseTabCopy {
  thesis: string;
  description: string;
  rightNote: string;
}

export const ENTERPRISE_TAB_COPY: Record<string, EnterpriseTabCopy> = {
  '/enterprise/portfolio': {
    thesis: 'The living portfolio.',
    description: 'All 174 initiatives, tracked and re-scored across every planning run — your system of record.',
    rightNote: 'Switch runs to see how priorities shifted over time.',
  },
  '/enterprise/scenarios': {
    thesis: 'Model the trade-offs.',
    description: 'Adjust budget and strategy, and watch the portfolio re-prioritize live against the funding line.',
    rightNote: 'Save a scenario to compare against the baseline.',
  },
  '/enterprise/decisions': {
    thesis: 'Every decision, on the record.',
    description: 'Timestamped, attributed, and auditable — how each initiative got where it is.',
    rightNote: 'Every entry links back to the run that produced it.',
  },
  '/enterprise/reports': {
    thesis: 'Governance-ready output.',
    description: 'Executive one-pager and board rationale, generated from the current scenario and decisions.',
    rightNote: 'Generated fresh from the current scenario and decisions.',
  },
};
